import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import { t } from '@owl-nest/localize'

import * as projectModel from '../../../../../models/project'
import { Shippings } from '../../../components/sidebar/Shipping'
import { useShippingInfo } from '../../../hooks/useShippingInfo'

type ShippingInfoProps = {
  project: projectModel.Project
  className?: string
}

export function ShippingInfo({ project, className }: ShippingInfoProps): React.ReactElement<ShippingInfoProps> {
  const shippingType = project.shippings.shipping_type

  if (shippingType !== 'physical-delivery') return <></>

  const { supportedCountries, selectedCountry, setSelectedCountry, shippingZone, rewardsHaveCustomDeliveries } =
    useShippingInfo(project)

  return (
    <>
      <ShippingDetails>
        <ShippingDetailsTitle>
          <plume.glyphs.stroke.Shipping size={22} />

          <Title>{t('Shipping methods')}</Title>
        </ShippingDetailsTitle>

        {supportedCountries.length > 1 ? (
          <plume.SelectField
            className={className}
            height={250}
            selected={[selectedCountry]}
            placeholder={t('Select a country')}
            filterable={supportedCountries.length > 12}
            filterPlaceholder={t('Search')}
            onChange={(selected) => {
              setSelectedCountry(selected[0].value)
            }}
          >
            {supportedCountries.map(({ label, code }) => {
              return <plume.Option key={code} value={code} label={label} />
            })}
          </plume.SelectField>
        ) : (
          <>
            {supportedCountries.length > 0 && (
              <plume.styles.copy.M.semiBold>{`${supportedCountries[0].label} ${t('only')}`}</plume.styles.copy.M.semiBold>
            )}
          </>
        )}

        {shippingZone && (
          <ShippingCosts>
            {(shippingZone.mondial_relay_delivery || shippingZone.home_delivery || shippingZone.pickup_points) && (
              <Shippings zone={shippingZone} currency={project.currency} />
            )}

            {rewardsHaveCustomDeliveries && (
              <plume.styles.copy.XS.regular>
                {t(
                  'Warning, rewards can have specifics delivery settings, costs and countries of delivery can then differ.',
                )}
              </plume.styles.copy.XS.regular>
            )}
          </ShippingCosts>
        )}
      </ShippingDetails>
    </>
  )
}

const Title = styled(plume.styles.heading.XXXXXXS)`
  color: ${plume.COLORS.TEXT_SECONDARY};
  margin-left: 10px;
  text-transform: uppercase;
`

const ShippingDetails = styled.div`
  margin-top: 26px;

  ${plume.styles.copy.XS.regular} {
    margin-top: 15px;
    margin-left: 16px;
  }

  ${plume.SelectField} {
    flex-direction: row;
    justify-content: space-between;

    ${plume.Select} {
      z-index: 3;
      position: relative;
      width: 200px;
    }
  }

  ${plume.styles.copy.M.semiBold} {
    margin-left: 16px;
  }
`

const ShippingDetailsTitle = styled.div`
  margin-bottom: 15px;
  align-items: center;
  display: flex;

  svg {
    color: ${plume.COLORS.PRIMARY_SAND_500};
  }
`

const ShippingCosts = styled.div`
  margin-top: 8px;
`
